/**
 * Top Header (STEP 5) — hospital/branch/workspace switchers, quick
 * search, notifications, user menu. Everything here is real, wired
 * client state (Zustand) — the switchers themselves list only what
 * `store/hospital.store.ts`/`store/branch.store.ts` currently hold
 * (no HMIS module exists yet to populate them from a real `GET
 * /hospitals`/`GET /branches` call — that wiring is Stage F1+'s own
 * job, once a real page needs it).
 */
import { useState, type JSX, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, IconButton, Typography, Box, Menu, MenuItem, Avatar, Divider, ListItemIcon, Tooltip, Chip, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import { useLayoutStore } from '@store/layout.store';
import { useThemeStore } from '@store/theme.store';
import { useAuth } from '@hooks/useAuth';
import { NotificationCenter } from '@components/navigation/NotificationCenter';
import { TextSizeControl } from './TextSizeControl';
import { useCurrentFinancialYear } from '@features/administration/hooks/useFinancialYear';
import { useCorrectionWorkflowMe } from '@features/billing/hooks/useCorrectionWorkflow';
import { useCanRequestCorrection } from '@hooks/usePermission';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { HospitalBranchSwitcher } from './HospitalBranchSwitcher';

const HEADER_HEIGHT = 48;

export function Header(): JSX.Element {
  const toggleSidebar = useLayoutStore((state) => state.toggleSidebar);
  const toggleMobileSidebar = useLayoutStore((state) => state.toggleMobileSidebar);
  const setQuickSearchOpen = useLayoutStore((state) => state.setQuickSearchOpen);
  const mode = useThemeStore((state) => state.mode);
  const setMode = useThemeStore((state) => state.setMode);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const { data: financialYear } = useCurrentFinancialYear();
  // The corrections link (desk, 2026-10-05): shown to someone selected as a verifier, and to anyone who can enter a request. Its count is
  // what waits for them to VERIFY. Approving is in Modifications (desk, 2026-10-07), which has its own link below — shown to selected
  // approvers only, so neither link counts the other stage's work.
  const correctionWorkflow = useCorrectionWorkflowMe();
  const canRequestCorrection = useCanRequestCorrection();
  const selectedForCorrections = Boolean(correctionWorkflow.data?.isVerifier);
  const correctionsWaiting = correctionWorkflow.data?.awaitingMyVerification ?? 0;
  const isApprover = Boolean(correctionWorkflow.data?.isApprover);
  const approvalsWaiting = correctionWorkflow.data?.awaitingMyApproval ?? 0;
  const theme = useTheme();
  // Below `sm`, Sidebar.tsx renders a temporary overlay drawer of its own
  // (`mobileSidebarOpen`) rather than the permanent icon rail this button
  // otherwise toggles — same breakpoint Sidebar.tsx itself checks.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initials = user ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase() : '';

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{ height: HEADER_HEIGHT, justifyContent: 'center', borderBottom: 1, borderColor: 'divider', zIndex: (theme) => theme.zIndex.drawer + 1 }}
    >
      <Toolbar variant="dense" sx={{ minHeight: HEADER_HEIGHT, gap: 1 }}>
        <IconButton edge="start" aria-label="Toggle navigation" size="small" onClick={isMobile ? toggleMobileSidebar : toggleSidebar}>
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" component="div" sx={{ mr: 2, whiteSpace: 'nowrap' }}>
          NEXAO HMIS
        </Typography>

        {/* Hidden below `sm` — real horizontal overflow at phone widths otherwise (confirmed live,
            Stage RC1); a phone user can still switch branch via Quick Actions/Global Search. */}
        <Box display={{ xs: 'none', sm: 'flex' }} gap={1} alignItems="center">
          <HospitalBranchSwitcher />
          <WorkspaceSwitcher />
          {financialYear && (
            <Tooltip title="Current financial year">
              <Chip size="small" variant="outlined" label={`FY ${financialYear.label}`} />
            </Tooltip>
          )}
          {(selectedForCorrections || canRequestCorrection) && (
            <Tooltip title={correctionsWaiting > 0 ? `${correctionsWaiting} correction request${correctionsWaiting === 1 ? '' : 's'} waiting for you` : 'Receipt corrections'}>
              <Chip
                size="small"
                variant={correctionsWaiting > 0 ? 'filled' : 'outlined'}
                color={correctionsWaiting > 0 ? 'warning' : 'default'}
                clickable
                label={correctionsWaiting > 0 ? `Corrections (${correctionsWaiting})` : 'Corrections'}
                onClick={() => navigate('/corrections')}
              />
            </Tooltip>
          )}
          {isApprover && (
            <Tooltip title={approvalsWaiting > 0 ? `${approvalsWaiting} correction${approvalsWaiting === 1 ? '' : 's'} waiting for your approval` : 'Modifications'}>
              <Chip
                size="small"
                variant={approvalsWaiting > 0 ? 'filled' : 'outlined'}
                color={approvalsWaiting > 0 ? 'warning' : 'default'}
                clickable
                label={approvalsWaiting > 0 ? `Modifications (${approvalsWaiting})` : 'Modifications'}
                onClick={() => navigate('/modifications/correction')}
              />
            </Tooltip>
          )}
        </Box>

        <Box flexGrow={1} />

        <Tooltip title="Quick search (Ctrl+K)">
          <IconButton aria-label="Quick search" size="small" onClick={() => setQuickSearchOpen(true)}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Toggle color mode">
          <IconButton aria-label="Toggle color mode" size="small" onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}>
            <Brightness4Icon fontSize="small" />
          </IconButton>
        </Tooltip>

        <NotificationCenter />

        <TextSizeControl />

        <Tooltip title={user ? `${user.firstName} ${user.lastName}` : 'Account'}>
          <IconButton aria-label="Account menu" size="small" onClick={(event: MouseEvent<HTMLElement>) => setUserMenuAnchor(event.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8125rem' }}>{initials || <PersonIcon fontSize="small" />}</Avatar>
          </IconButton>
        </Tooltip>
        <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
          <Box px={2} py={1}>
            <Typography variant="body2" fontWeight={600}>
              {user ? `${user.firstName} ${user.lastName}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              navigate('/profile');
            }}
          >
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            My Profile
          </MenuItem>
          <Divider />
          <MenuItem onClick={logout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Log out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export { HEADER_HEIGHT };
